// Scale-Pak: scale tickets from every MRF, organics site and transfer facility,
// read from the Soft-Pak replica through /scale-pak (backend/src/routes/scalePak.js).
// Replaces the 30 Scale-Pak Crystal reports; the rules behind cost center,
// stream and origin are in GitLab db2-replica/softpak-reporting docs/scale-pak.md.
//
// The daily totals arrive once per date range and facility; every other filter
// (cost center, direction, hauler, stream) is applied here, so changing one
// needs no new request.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowDownToLine, ArrowUpFromLine, Truck, Scale, DollarSign, XCircle, Download, Recycle, Sprout, Ruler, Package, Mountain, Target, TrendingUp } from 'lucide-react';
import { api } from '../services/api';
import PageHeader from '../components/Shared/PageHeader';
import StatTile from '../components/Inventory/StatTile';
import { SelectOne, TextField } from '../components/Expenses/FilterBar';
import { fmtDate, fmtMoney, fmtMonth, fmtNumber } from '../components/Inventory/format';
import { btn, c, card, chart, t, tbl } from '../theme';

const RANGES = [
  { value: '1', label: 'Today' },
  { value: 'Y', label: 'Yesterday' },
  { value: '7', label: 'Last 7 days' },
  { value: 'MTD', label: 'Month to date' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'C', label: 'Custom…' },
];
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'inbound', label: 'Inbound watch' },
  { id: 'commodity', label: 'Commodity sales' },
  { id: 'organics', label: 'Organics' },
  { id: 'mrf', label: 'MRF in/out' },
  { id: 'landfill', label: 'Landfill (SBC)' },
  { id: 'tickets', label: 'Tickets' },
];
const DIRECTIONS = [{ value: '', label: 'Both' }, { value: 'I', label: 'Inbound' }, { value: 'O', label: 'Outbound' }];
const HAULERS = ['Athens', '3rd Party', 'Mini MRF'];
const IN = chart.categorical[0];
const OUT = chart.categorical[1];
const HEAT = ['#EEF2F8', ...chart.sequential];
const HOURS = Array.from({ length: 15 }, (_, i) => i + 4);
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const n = (v) => Number(v || 0);
const iso = (d) => d.toISOString().slice(0, 10);

export function pacificToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}

function addDays(day, k) {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + k);
  return iso(d);
}

// The dates a preset covers, and the same length just before it (for the change figures).
export function rangeFor(preset, today = pacificToday(), custom = {}) {
  let from = today;
  let to = today;
  if (preset === 'Y') { from = addDays(today, -1); to = from; }
  else if (preset === 'MTD') from = `${today.slice(0, 8)}01`;
  else if (preset === 'C') { from = custom.from || today; to = custom.to || today; }
  else from = addDays(today, -(Number(preset) - 1));
  const days = Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;
  return { from, to, days, prevFrom: addDays(from, -days), prevTo: addDays(from, -1) };
}

// Daily rows after the in-page filters.
export function applyFilters(rows, f) {
  return rows.filter((r) => (!f.costCenter || r.cost_center_code === f.costCenter)
    && (!f.direction || r.direction === f.direction)
    && (!f.hauler || r.hauler === f.hauler)
    && (!f.stream || r.stream_code === f.stream));
}

export function totals(rows) {
  const out = { tonsIn: 0, tonsOut: 0, loadsIn: 0, loadsOut: 0, charges: 0, voids: 0, disposal: 0 };
  for (const r of rows) {
    if (r.direction === 'I') { out.tonsIn += n(r.tons); out.loadsIn += n(r.loads); }
    else { out.tonsOut += n(r.tons); out.loadsOut += n(r.loads); }
    out.charges += n(r.charges);
    out.voids += n(r.void_lines);
    out.disposal += n(r.disposal_cost);
  }
  return out;
}

function sumBy(rows, key, label) {
  const m = new Map();
  for (const r of rows) {
    const k = r[key];
    const cur = m.get(k) || { key: k, label: label(r), tons: 0, loads: 0 };
    cur.tons += n(r.tons);
    cur.loads += n(r.loads);
    m.set(k, cur);
  }
  return [...m.values()].filter((x) => x.tons > 0).sort((a, b) => b.tons - a.tons);
}

function byDay(rows, from, to) {
  const days = new Map();
  for (let d = from; d <= to; d = addDays(d, 1)) days.set(d, { day: d, inbound: 0, outbound: 0 });
  for (const r of rows) {
    const d = days.get(String(r.ticket_date).slice(0, 10));
    if (d) d[r.direction === 'I' ? 'inbound' : 'outbound'] += n(r.tons);
  }
  return [...days.values()].map((d) => ({ ...d, inbound: Math.round(d.inbound), outbound: Math.round(d.outbound) }));
}

const dayLabel = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'UTC' });

export default function ScalePak() {
  const [tab, setTab] = useState('overview');
  const [preset, setPreset] = useState('30');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [facility, setFacility] = useState('');
  const [costCenter, setCostCenter] = useState('');
  const [direction, setDirection] = useState('');
  const [hauler, setHauler] = useState('');
  const [stream, setStream] = useState('');

  const range = useMemo(() => rangeFor(preset, pacificToday(), custom), [preset, custom]);
  const onlyToday = range.days === 1 && range.to === pacificToday();
  const base = { from: range.from, to: range.to, ...(facility ? { facility } : {}) };

  const daily = useQuery({
    queryKey: ['scale-pak', 'daily', base],
    queryFn: () => api.get('/scale-pak/daily', { params: base }),
    staleTime: 60_000,
    refetchInterval: range.to === pacificToday() ? 120_000 : false,
  });
  const prevBase = { from: range.prevFrom, to: range.prevTo, ...(facility ? { facility } : {}) };
  const prev = useQuery({
    queryKey: ['scale-pak', 'daily', prevBase],
    queryFn: () => api.get('/scale-pak/daily', { params: prevBase }),
    staleTime: 10 * 60_000,
    // Today alone is a part day: against all of yesterday every figure would read as a drop,
    // so it shows no change until the day is over.
    enabled: range.days <= 200 && !onlyToday,
  });
  const hourly = useQuery({
    queryKey: ['scale-pak', 'hourly', base],
    queryFn: () => api.get('/scale-pak/hourly', { params: base }),
    staleTime: 60_000,
    enabled: tab === 'overview',
  });

  const rows = useMemo(() => daily.data?.rows || [], [daily.data]);
  const filters = { costCenter, direction, hauler, stream };
  const shown = useMemo(() => applyFilters(rows, filters), [rows, costCenter, direction, hauler, stream]); // eslint-disable-line react-hooks/exhaustive-deps
  const before = useMemo(() => applyFilters(prev.data?.rows || [], filters), [prev.data, costCenter, direction, hauler, stream]); // eslint-disable-line react-hooks/exhaustive-deps
  const now = totals(shown);
  const was = prev.data && !onlyToday ? totals(before) : null;

  // Pick lists from what the date range holds.
  const facilities = useMemo(() => {
    const m = new Map(rows.map((r) => [r.facility_code, r.facility_name]));
    if (facility && !m.has(facility)) m.set(facility, facility);
    return [...m.entries()].sort().map(([v, l]) => ({ value: v, label: `${v} · ${l}` }));
  }, [rows, facility]);
  const costCenters = useMemo(() => sumBy(applyFilters(rows, { ...filters, costCenter: '' }), 'cost_center_code', (r) => `${r.cost_center_code} ${r.cost_center_name}`), [rows, direction, hauler, stream]); // eslint-disable-line react-hooks/exhaustive-deps
  const streams = useMemo(() => sumBy(applyFilters(rows, { ...filters, stream: '' }), 'stream_code', (r) => `${r.stream_code} ${r.stream_name}`), [rows, costCenter, direction, hauler]); // eslint-disable-line react-hooks/exhaustive-deps
  const series = useMemo(() => byDay(shown, range.from, range.to), [shown, range.from, range.to]);

  const voidsOnly = () => setTab('tickets');

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Scale-Pak"
        subtitle="Scale tickets from every MRF, organics site and transfer facility, read live from Soft-Pak. Replaces the 30 Scale-Pak Crystal reports."
        meta={<span style={s.live}><span style={s.dot} />Live from the Soft-Pak replica · past days refresh at 3:15 AM and 9:15 PM</span>}
      />

      <div style={card.base}>
        <div style={s.filters}>
          <SelectOne label="Dates" value={preset} onChange={setPreset} options={RANGES} width={150} />
          {preset === 'C' && (
            <>
              <TextField label="From" type="date" value={custom.from} onChange={(v) => setCustom((x) => ({ ...x, from: v }))} width={150} />
              <TextField label="To" type="date" value={custom.to} onChange={(v) => setCustom((x) => ({ ...x, to: v }))} width={150} />
            </>
          )}
          <SelectOne label="Facility" value={facility} onChange={setFacility} options={[{ value: '', label: 'All' }, ...facilities]} width={200} />
          <SelectOne label="Cost center" value={costCenter} onChange={setCostCenter}
                     options={[{ value: '', label: 'All' }, ...costCenters.map((x) => ({ value: x.key, label: x.label })),
                       ...(costCenter && !costCenters.some((x) => x.key === costCenter) ? [{ value: costCenter, label: costCenter }] : [])]} width={210} />
          <div style={s.field}>
            <span style={s.label}>Direction</span>
            <div style={s.seg} role="group" aria-label="Direction">
              {DIRECTIONS.map((d) => (
                <button key={d.value} type="button" aria-pressed={direction === d.value}
                        style={{ ...s.segBtn, ...(direction === d.value ? s.segOn : null) }} onClick={() => setDirection(d.value)}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <SelectOne label="Hauler" value={hauler} onChange={setHauler} options={[{ value: '', label: 'All' }, ...HAULERS.map((h) => ({ value: h, label: h }))]} width={130} />
          <SelectOne label="Material stream" value={stream} onChange={setStream}
                     options={[{ value: '', label: 'All' }, ...streams.map((x) => ({ value: x.key, label: x.label })),
                       ...(stream && !streams.some((x) => x.key === stream) ? [{ value: stream, label: stream }] : [])]} width={220} />
        </div>

        <div style={s.tabRow} role="tablist">
          {TABS.map((x) => (
            <button key={x.id} type="button" role="tab" aria-selected={tab === x.id}
                    style={{ ...s.tab, ...(tab === x.id ? s.tabOn : null) }} onClick={() => setTab(x.id)}>
              {x.label}
            </button>
          ))}
        </div>

        {daily.isError && <div style={s.error}>{daily.error.message}</div>}

        {tab === 'overview' && (
          <div style={s.panel}>
            <div style={s.tiles}>
              <StatTile icon={<ArrowDownToLine size={16} />} label="Inbound tons" value={fmtNumber(Math.round(now.tonsIn))} sub={daily.isLoading ? 'Loading…' : onlyToday ? 'so far today' : `${range.days} day${range.days === 1 ? '' : 's'}`} current={now.tonsIn} previous={was?.tonsIn} />
              <StatTile icon={<ArrowUpFromLine size={16} />} label="Outbound tons" value={fmtNumber(Math.round(now.tonsOut))} sub={onlyToday ? 'compared once the day is over' : 'vs the same length before'} current={now.tonsOut} previous={was?.tonsOut} />
              <StatTile icon={<Truck size={16} />} label="Inbound loads" value={fmtNumber(now.loadsIn)} sub="one per ticket" current={now.loadsIn} previous={was?.loadsIn} />
              <StatTile icon={<Scale size={16} />} label="Average tons per inbound load" value={now.loadsIn ? (now.tonsIn / now.loadsIn).toFixed(2) : '—'} sub="weight lines only" />
              <StatTile icon={<DollarSign size={16} />} tone="success" label="Charges" value={fmtMoney(now.charges)} sub="ticket totals, before tax" current={now.charges} previous={was?.charges} />
              <StatTile icon={<XCircle size={16} />} tone={now.voids ? 'warning' : 'neutral'} label="Voided lines" value={fmtNumber(now.voids)} sub="open the ticket list" onClick={voidsOnly} />
            </div>

            <div style={s.grid2}>
              <div style={{ ...card.sm, minWidth: 0 }}>
                <div style={s.cardHead}>Tons per day<Legend items={[['Inbound', IN], ['Outbound', OUT]]} /></div>
                <div style={s.chartBody}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid vertical={false} stroke={chart.grid} />
                      <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} minTickGap={18} />
                      <YAxis tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => fmtNumber(v)} />
                      <Tooltip cursor={{ fill: 'rgba(27,42,74,0.05)' }} labelFormatter={(d) => fmtDate(d)}
                               formatter={(v, name) => [`${fmtNumber(v)} t`, name === 'inbound' ? 'Inbound' : 'Outbound']}
                               contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${chart.tooltipBorder}` }} />
                      <Bar dataKey="inbound" stackId="t" fill={IN} isAnimationActive={false} />
                      <Bar dataKey="outbound" stackId="t" fill={OUT} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div style={{ ...card.sm, minWidth: 0 }}>
                <div style={s.cardHead}>Tons by cost center<span style={s.headNote}>Click a bar to filter</span></div>
                <div style={s.chartBody}><Ranked items={costCenters} selected={costCenter} onPick={(k) => setCostCenter(costCenter === k ? '' : k)} /></div>
              </div>
            </div>

            <div style={s.grid2b}>
              <div style={{ ...card.sm, minWidth: 0 }}>
                <div style={s.cardHead}>Tons by material stream<span style={s.headNote}>Click a bar to filter</span></div>
                <div style={s.chartBody}><Ranked items={streams} selected={stream} onPick={(k) => setStream(stream === k ? '' : k)} /></div>
              </div>
              <div style={{ ...card.sm, minWidth: 0 }}>
                <div style={s.cardHead}>Inbound loads by hour<span style={s.headNote}>Average per day of the week</span></div>
                <div style={s.chartBody}><Heatmap rows={hourly.data?.rows || []} costCenter={costCenter} loading={hourly.isLoading} /></div>
              </div>
            </div>
          </div>
        )}

        {tab === 'inbound' && <InboundWatch rows={rows} range={range} facility={facility} />}
        {tab === 'commodity' && <Commodity range={range} facility={facility} />}
        {tab === 'organics' && <Organics range={range} facility={facility} />}
        {tab === 'mrf' && <MrfInOut range={range} facility={facility} />}
        {tab === 'landfill' && <Landfill />}

        {tab === 'tickets' && (
          <Tickets range={range} facility={facility} costCenter={costCenter} direction={direction} hauler={hauler} stream={stream} />
        )}
      </div>
    </div>
  );
}

function Legend({ items }) {
  return (
    <span style={s.legend}>
      {items.map(([label, color]) => (
        <span key={label} style={s.legendItem}><i style={{ ...s.swatch, background: color }} />{label}</span>
      ))}
    </span>
  );
}

// Magnitude for one nominal dimension: one navy hue; a picked bar stays navy, the rest recede.
function Ranked({ items, selected, onPick }) {
  if (!items.length) return <div style={s.empty}>No tons for these filters.</div>;
  const max = Math.max(...items.map((x) => x.tons), 1);
  return (
    <div style={s.ranked}>
      {items.slice(0, 14).map((x) => (
        <button key={x.key} type="button" style={s.rankRow} onClick={() => onPick(x.key)} aria-pressed={selected === x.key}
                title={`${x.label}: ${fmtNumber(Math.round(x.tons))} t, ${fmtNumber(x.loads)} loads`}>
          <span style={s.rankLabel}>{x.label}</span>
          <span style={s.rankTrack}>
            <span style={{ ...s.rankBar, width: `${(x.tons / max) * 100}%`, background: selected && selected !== x.key ? chart.deselectedLight : chart.emphasis }} />
          </span>
          <span style={s.rankValue}>{fmtNumber(Math.round(x.tons))} t</span>
        </button>
      ))}
      {items.length > 14 && <div style={s.note}>{items.length - 14} more in the list above.</div>}
    </div>
  );
}

function Heatmap({ rows, costCenter, loading }) {
  const grid = useMemo(() => {
    const loads = new Map();
    const days = new Map();
    for (const r of rows) {
      if (costCenter && r.cost_center_code !== costCenter) continue;
      const k = `${r.weekday}:${r.hour_in}`;
      loads.set(k, (loads.get(k) || 0) + n(r.loads));
      days.set(r.weekday, Math.max(days.get(r.weekday) || 0, n(r.days)));
    }
    const cells = WEEKDAYS.map((_, i) => HOURS.map((h) => {
      const d = days.get(i + 1) || 0;
      return d ? (loads.get(`${i + 1}:${h}`) || 0) / d : 0;
    }));
    return { cells, max: Math.max(1, ...cells.flat()) };
  }, [rows, costCenter]);
  if (loading) return <div style={s.empty}>Loading…</div>;
  return (
    <div>
      <div style={s.heatRow}>
        <span />
        {HOURS.map((h) => <span key={h} style={s.heatHour}>{h % 12 || 12}{h < 12 ? 'a' : 'p'}</span>)}
      </div>
      {WEEKDAYS.map((w, i) => (
        <div key={w} style={s.heatRow}>
          <span style={s.heatDay}>{w}</span>
          {grid.cells[i].map((v, j) => (
            <span key={j} title={`${w} ${HOURS[j] % 12 || 12} ${HOURS[j] < 12 ? 'am' : 'pm'}: ${v.toFixed(1)} loads on average`}
                  style={{ ...s.heatCell, background: v ? HEAT[Math.min(HEAT.length - 1, 1 + Math.floor((v / grid.max) * (HEAT.length - 1.001)))] : HEAT[0] }} />
          ))}
        </div>
      ))}
      <div style={{ ...s.legend, marginTop: 8 }}>
        <span>Fewer</span>{HEAT.slice(1).map((col) => <i key={col} style={{ ...s.swatch, background: col }} />)}<span>More loads</span>
      </div>
    </div>
  );
}

function Tickets({ range, facility, costCenter, direction, hauler, stream }) {
  const [voids, setVoids] = useState('EXCLUDE');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const tooLong = range.days > 93;
  const params = {
    from: range.from, to: range.to,
    ...(facility ? { facility } : {}), ...(costCenter ? { cost_center: costCenter } : {}),
    ...(direction ? { direction } : {}), voids,
  };
  const q = useQuery({
    queryKey: ['scale-pak', 'tickets', params],
    queryFn: () => api.get('/scale-pak/tickets', { params }),
    enabled: !tooLong,
    staleTime: 60_000,
  });
  const lines = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (q.data?.rows || []).filter((r) => (!hauler || r.hauler === hauler) && (!stream || r.stream_code === stream)
      && (!term || [r.ticket, r.customer_name, r.customer_number, r.origin, r.material_desc, r.vehicle, r.ticket_route]
        .some((v) => String(v ?? '').toLowerCase().includes(term))));
  }, [q.data, hauler, stream, search]);

  async function exportXlsx() {
    setExporting(true);
    try {
      const blob = await api.getBlob('/scale-pak/tickets/export.xlsx', { params });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scale-pak-tickets-${range.from}-to-${range.to}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  if (tooLong) return <div style={s.panel}><div style={s.empty}>The ticket list covers up to 93 days. Choose a shorter range.</div></div>;
  return (
    <div style={s.panel}>
      <div style={s.toolbar}>
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticket, customer, origin, material, vehicle, route"
               style={s.search} aria-label="Search tickets" />
        <SelectOne label="Voids" value={voids} onChange={setVoids} width={150}
                   options={[{ value: 'EXCLUDE', label: 'Leave out' }, { value: 'INCLUDE', label: 'Include' }, { value: 'ONLY', label: 'Voids only' }]} />
        <span style={s.note}>{q.isLoading ? 'Loading…' : `${fmtNumber(lines.length)} lines${q.data?.truncated ? ' (first 50,000; narrow the filters)' : ''}`}</span>
        <button type="button" style={btn.secondarySm} onClick={exportXlsx} disabled={exporting || !lines.length}>
          <Download size={13} />{exporting ? 'Exporting…' : 'Export Excel'}
        </button>
      </div>
      {q.isError && <div style={s.error}>{q.error.message}</div>}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              {['Ticket', 'Date', 'In', 'Facility', 'Dir', 'Customer', 'Origin', 'Cost ctr', 'Material', 'Hauler'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}
              {['Tons', 'Charge'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {lines.slice(0, 500).map((r) => (
              <tr key={`${r.facility_code}-${r.ticket}-${r.line}`} style={{ ...tbl.tr, ...(r.is_void ? s.voidRow : null) }}>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.ticket}{r.line > 1 ? `/${r.line}` : ''}</td>
                <td style={tbl.tdSm}>{fmtDate(r.ticket_date)}</td>
                <td style={tbl.tdSm}>{r.time_in ? String(r.time_in).slice(0, 5) : '—'}</td>
                <td style={tbl.tdSm}>{r.facility_code}</td>
                <td style={tbl.tdSm}>{r.direction}</td>
                <td style={tbl.tdSm}>{r.customer_name || r.customer_number}</td>
                <td style={tbl.tdSm}>{r.origin || '—'}</td>
                <td style={tbl.tdSm} title={`${r.cost_center_name} (${r.cost_center_rule})`}>{r.cost_center_code}</td>
                <td style={tbl.tdSm}>{r.material_desc || r.material_code}</td>
                <td style={tbl.tdSm}>{r.hauler}</td>
                <td style={tbl.tdRSm}>{n(r.tons) ? n(r.tons).toFixed(2) : '—'}</td>
                <td style={tbl.tdRSm}>{fmtMoney(r.charge)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lines.length > 500 && <div style={s.note}>Showing the first 500 lines. Export Excel has all {fmtNumber(lines.length)}.</div>}
    </div>
  );
}

// ── Inbound watch: daily permit headroom, and one day's MRF Inbound checks ──────────────
const WATCH_CHECKS = [
  ['over_13_tons', 'Loads of 13 tons or more', 'FV, GV, TP or TV from yard customers'],
  ['before_7am', 'MSW loads before 7 am', 'TP or TV from yard customers, roll-off and sweeper left out'],
  ['last_five', 'Last five loads of the day', 'Yard customers, per facility'],
];

function InboundWatch({ rows, range, facility }) {
  const [day, setDay] = useState(range.to);
  const permits = useQuery({ queryKey: ['scale-pak', 'permits'], queryFn: () => api.get('/scale-pak/permits'), staleTime: 10 * 60_000 });
  const watchParams = { date: day, ...(facility ? { facility } : {}) };
  const watch = useQuery({
    queryKey: ['scale-pak', 'inbound-watch', watchParams],
    queryFn: () => api.get('/scale-pak/inbound-watch', { params: watchParams }),
    staleTime: 60_000,
  });
  // Inbound tons per day for each facility with a permit, from the daily totals already loaded
  // (all hauler, cost center and stream filters ignored: a permit counts everything).
  const permitFacilities = (permits.data?.rows || []).filter((p) => !facility || p.facility_code === facility);
  const series = useMemo(() => permitFacilities.map((p) => {
    const byDay = new Map();
    for (let d = range.from; d <= range.to; d = addDays(d, 1)) byDay.set(d, 0);
    for (const r of rows) {
      if (r.facility_code === p.facility_code && r.direction === 'I') {
        const d = String(r.ticket_date).slice(0, 10);
        if (byDay.has(d)) byDay.set(d, byDay.get(d) + n(r.tons));
      }
    }
    const name = rows.find((r) => r.facility_code === p.facility_code)?.facility_name || p.facility_code;
    const days = [...byDay.entries()].map(([d, tons]) => ({ day: d, tons: Math.round(tons) }));
    return { ...p, permit: n(p.daily_permit_tons), name, days, over: days.filter((x) => x.tons > n(p.daily_permit_tons)).length };
  }), [permitFacilities.map((p) => p.facility_code).join(), rows, range.from, range.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const loads = watch.data?.rows || [];
  return (
    <div style={s.panel}>
      <div style={s.grid2b}>
        {series.length === 0 && <div style={s.empty}>No daily permit is set for this facility.</div>}
        {series.map((p) => (
          <div key={p.facility_code} style={{ ...card.sm, minWidth: 0 }}>
            <div style={s.cardHead}>
              {p.facility_code} · {p.name}: inbound tons against the {fmtNumber(p.permit)} t permit
              <span style={p.over ? s.badgeWarn : s.badgeOk}>{p.over ? `Over on ${p.over} day${p.over === 1 ? '' : 's'}` : 'Within permit'}</span>
            </div>
            <div style={s.chartBody}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={p.days} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid vertical={false} stroke={chart.grid} />
                  <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} minTickGap={18} />
                  <YAxis tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => fmtNumber(v)} />
                  <Tooltip cursor={{ fill: 'rgba(27,42,74,0.05)' }} labelFormatter={(d) => fmtDate(d)}
                           formatter={(v) => [`${fmtNumber(v)} t (${fmtNumber(Math.abs(p.permit - v))} t ${v > p.permit ? 'over' : 'left'})`, 'Inbound']}
                           contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${chart.tooltipBorder}` }} />
                  <ReferenceLine y={p.permit} stroke={c.dangerText} strokeDasharray="5 4" />
                  <Bar dataKey="tons" fill={IN} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
              <div style={s.note}>Dashed line: the daily permit (draft until operations confirm it).</div>
            </div>
          </div>
        ))}
      </div>

      <div style={s.toolbar}>
        <TextField label="Checks for" type="date" value={day} onChange={(v) => setDay(v || range.to)} width={160} />
        <span style={s.note}>{watch.isLoading ? 'Loading…' : `${fmtNumber(loads.length)} inbound loads that day`}</span>
      </div>
      {watch.isError && <div style={s.error}>{watch.error.message}</div>}
      <div style={s.grid2b}>
        {WATCH_CHECKS.map(([key, title, sub]) => {
          const hits = loads.filter((r) => r[key]);
          return (
            <div key={key} style={{ ...card.sm, minWidth: 0 }}>
              <div style={s.cardHead}>{title}<span style={s.headNote}>{sub}</span></div>
              <div style={s.tableWrapFlat}>
                <table style={s.table}>
                  <thead><tr>{['In', 'Ticket', 'Facility', 'Customer', 'Material', 'Route', 'Vehicle'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}<th style={tbl.thRSm}>Tons</th></tr></thead>
                  <tbody>
                    {hits.map((r) => (
                      <tr key={`${r.facility_code}-${r.ticket}`} style={tbl.tr}>
                        <td style={tbl.tdSm}>{r.time_in ? String(r.time_in).slice(0, 5) : '—'}</td>
                        <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.ticket}</td>
                        <td style={tbl.tdSm}>{r.facility_code}</td>
                        <td style={tbl.tdSm}>{r.customer_name || r.customer_number}</td>
                        <td style={tbl.tdSm}>{r.material_desc || r.material_code}</td>
                        <td style={tbl.tdSm}>{r.ticket_route || '—'}</td>
                        <td style={tbl.tdSm}>{r.vehicle || '—'}</td>
                        <td style={tbl.tdRSm}>{n(r.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                    {!hits.length && <tr><td colSpan={8} style={{ ...tbl.tdSm, color: c.textFaint }}>None that day.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Commodity sales and Organics: the materials totals, two ways ────────────────────────
function useMaterials(kind, range, facility) {
  const params = { from: range.from, to: range.to, kind, ...(facility ? { facility } : {}) };
  return useQuery({
    queryKey: ['scale-pak', 'materials', params],
    queryFn: () => api.get('/scale-pak/materials', { params }),
    staleTime: 60_000,
  });
}

function groupSum(rows, keyOf, fields) {
  const m = new Map();
  for (const r of rows) {
    const k = keyOf(r);
    const cur = m.get(k) || Object.fromEntries([['key', k], ...fields.map((f) => [f, 0])]);
    for (const f of fields) cur[f] += n(r[f]);
    m.set(k, cur);
  }
  return [...m.values()];
}

function Commodity({ range, facility }) {
  const q = useMaterials('COMMODITY', range, facility);
  const rows = q.data?.rows || [];
  const fields = ['quantity', 'commodity_amount', 'loads'];
  const total = groupSum(rows, () => 'all', fields)[0] || { quantity: 0, commodity_amount: 0, loads: 0 };
  const families = groupSum(rows, (r) => r.commodity_family, fields).sort((a, b) => b.quantity - a.quantity);
  const grades = groupSum(rows, (r) => `${r.commodity_family}|${r.commodity_grade}`, fields).sort((a, b) => b.quantity - a.quantity);
  const errors = rows.filter((r) => r.commodity_grade === 'ERROR');
  return (
    <div style={s.panel}>
      {q.isError && <div style={s.error}>{q.error.message}</div>}
      <div style={s.tiles}>
        <StatTile icon={<Recycle size={16} />} label="Commodity tons sold" value={fmtNumber(Math.round(total.quantity))} sub="outbound to destination CS" />
        <StatTile icon={<DollarSign size={16} />} tone="success" label="Sales amount" value={fmtMoney(total.commodity_amount)} sub="rate plus adder, times tons" />
        <StatTile icon={<Scale size={16} />} label="Average per ton" value={total.quantity ? fmtMoney(total.commodity_amount / total.quantity) : '—'} sub="all grades" />
        <StatTile icon={<Truck size={16} />} label="Outbound loads" value={fmtNumber(total.loads)} sub="one per ticket" />
      </div>
      <div style={s.grid2b}>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Tons by family<span style={s.headNote}>{q.isLoading ? 'Loading…' : `${range.days} day${range.days === 1 ? '' : 's'}`}</span></div>
          <div style={s.chartBody}><Ranked items={families.map((x) => ({ key: x.key, label: x.key, tons: x.quantity, loads: x.loads }))} selected="" onPick={() => {}} /></div>
        </div>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Price per ton by grade<span style={s.headNote}>{errors.length ? `${errors.length} line(s) with a material no grade covers` : 'every material has a grade'}</span></div>
          <div style={s.tableWrapFlat}>
            <table style={s.table}>
              <thead><tr><th style={tbl.thSm}>Grade</th><th style={tbl.thSm}>Family</th><th style={tbl.thRSm}>Tons</th><th style={tbl.thRSm}>$/ton</th><th style={tbl.thRSm}>Amount</th></tr></thead>
              <tbody>
                {grades.map((g) => {
                  const [family, grade] = g.key.split('|');
                  return (
                    <tr key={g.key} style={tbl.tr}>
                      <td style={{ ...tbl.tdSm, ...(grade === 'ERROR' ? { color: c.dangerText } : null) }}>{grade}</td>
                      <td style={tbl.tdSm}>{family}</td>
                      <td style={tbl.tdRSm}>{fmtNumber(Math.round(g.quantity))}</td>
                      <td style={tbl.tdRSm}>{g.quantity ? fmtMoney(g.commodity_amount / g.quantity) : '—'}</td>
                      <td style={tbl.tdRSm}>{fmtMoney(g.commodity_amount)}</td>
                    </tr>
                  );
                })}
                {!grades.length && <tr><td colSpan={5} style={{ ...tbl.tdSm, color: c.textFaint }}>{q.isLoading ? 'Loading…' : 'No commodity sales in this range.'}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const BUCKETS = ['Compost', 'Mulch', 'Erosion control', 'Biomass'];
const PAY_TYPES = ['Cash', 'Check', 'Credit card', 'Billed', 'Other'];

function Organics({ range, facility }) {
  const q = useMaterials('ORGANICS', range, facility);
  const rows = q.data?.rows || [];
  const fields = ['tons', 'yards', 'loads', 'charges'];
  const total = groupSum(rows, () => 'all', fields)[0] || { tons: 0, yards: 0, loads: 0, charges: 0 };
  const byBucket = new Map(groupSum(rows, (r) => r.organics_bucket, fields).map((x) => [x.key, x]));
  const cells = new Map(groupSum(rows, (r) => `${r.organics_bucket}|${r.pay_type}`, ['charges']).map((x) => [x.key, x.charges]));
  return (
    <div style={s.panel}>
      {q.isError && <div style={s.error}>{q.error.message}</div>}
      <div style={s.tiles}>
        <StatTile icon={<Sprout size={16} />} label="Tons" value={fmtNumber(Math.round(total.tons))} sub="compost, mulch, erosion control, biomass" />
        <StatTile icon={<Ruler size={16} />} label="Cubic yards" value={fmtNumber(Math.round(total.yards))} sub="lines sold by the yard" />
        <StatTile icon={<Truck size={16} />} label="Loads" value={fmtNumber(total.loads)} sub="one per ticket" />
        <StatTile icon={<DollarSign size={16} />} tone="success" label="Charges" value={fmtMoney(total.charges)} sub="ticket totals, before tax" />
      </div>
      <div style={{ ...card.sm, minWidth: 0 }}>
        <div style={s.cardHead}>By product and payment<span style={s.headNote}>Payment from the scale point of sale: none means billed to the account</span></div>
        <div style={s.tableWrapFlat}>
          <table style={s.table}>
            <thead><tr>
              <th style={tbl.thSm}>Product</th><th style={tbl.thRSm}>Tons</th><th style={tbl.thRSm}>Yards</th><th style={tbl.thRSm}>Loads</th>
              {PAY_TYPES.map((p) => <th key={p} style={tbl.thRSm}>{p}</th>)}<th style={tbl.thRSm}>Charges</th>
            </tr></thead>
            <tbody>
              {BUCKETS.map((b) => {
                const x = byBucket.get(b) || { tons: 0, yards: 0, loads: 0, charges: 0 };
                return (
                  <tr key={b} style={tbl.tr}>
                    <td style={tbl.tdSm}>{b}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(Math.round(x.tons))}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(Math.round(x.yards))}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(x.loads)}</td>
                    {PAY_TYPES.map((p) => <td key={p} style={tbl.tdRSm}>{cells.get(`${b}|${p}`) ? fmtMoney(cells.get(`${b}|${p}`)) : '—'}</td>)}
                    <td style={tbl.tdRSm}>{fmtMoney(x.charges)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── MRF in/out: Crystal MRF-InOut, Scale Pak Rate, weight per bale, MRF Work Order Report ─
const money2 = (v) => Number(v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const perTon = (amount, tons) => (n(tons) ? money2(n(amount) / n(tons)) : '—');

// Material rows (kind ALL) folded into the MRF in/out tables.
export function mrfSummary(rows) {
  const out = rows.filter((r) => r.direction === 'O');
  const inb = rows.filter((r) => r.direction === 'I');
  const fields = ['tons', 'loads', 'charges', 'disposal_cost', 'net_lbs', 'bales'];
  const sum = (list) => groupSum(list, () => 'all', fields)[0] || Object.fromEntries(fields.map((f) => [f, 0]));
  const label = (r) => r.material_desc || r.material_code;
  return {
    inbound: sum(inb),
    outbound: sum(out),
    byDestination: groupSum(out.map((r) => ({ ...r, k: `${r.destination_code || '—'}|${r.destination_name || ''}|${label(r)}` })), (r) => r.k, fields)
      .map((x) => { const [code, name, material] = x.key.split('|'); return { ...x, code, name, material }; })
      .sort((a, b) => b.tons - a.tons),
    byMaterialIn: groupSum(inb, label, fields).sort((a, b) => b.tons - a.tons),
    bales: groupSum(rows.filter((r) => n(r.bales) > 0), label, fields).sort((a, b) => b.bales - a.bales),
  };
}

function MrfInOut({ range, facility }) {
  const [woStatus, setWoStatus] = useState('');
  const params = { from: range.from, to: range.to, kind: 'ALL', facility };
  const q = useQuery({
    queryKey: ['scale-pak', 'materials', params],
    queryFn: () => api.get('/scale-pak/materials', { params }),
    enabled: !!facility,
    staleTime: 60_000,
  });
  const rates = useQuery({
    queryKey: ['scale-pak', 'rates', facility],
    queryFn: () => api.get('/scale-pak/rates', { params: { facility } }),
    enabled: !!facility,
    staleTime: 10 * 60_000,
  });
  const woTooLong = range.days > 93;
  const woParams = { from: range.from, to: range.to, company: facility };
  const wo = useQuery({
    queryKey: ['scale-pak', 'work-orders', woParams],
    queryFn: () => api.get('/scale-pak/work-orders', { params: woParams }),
    enabled: !!facility && !woTooLong,
    staleTime: 60_000,
  });
  const m = useMemo(() => mrfSummary(q.data?.rows || []), [q.data]);
  const woLines = (wo.data?.rows || []).filter((r) => !woStatus || r.status === woStatus);

  if (!facility) return <div style={s.panel}><div style={s.empty}>Choose a facility above: MRF in/out is one MRF at a time, as in Crystal.</div></div>;
  // Only lines with a bale count: loose loads carry net lbs too and would inflate the weight.
  const baled = m.bales.reduce((acc, x) => ({ bales: acc.bales + x.bales, lbs: acc.lbs + x.net_lbs }), { bales: 0, lbs: 0 });
  const lbsPerBale = baled.bales ? baled.lbs / baled.bales : 0;
  return (
    <div style={s.panel}>
      {q.isError && <div style={s.error}>{q.error.message}</div>}
      <div style={s.tiles}>
        <StatTile icon={<ArrowDownToLine size={16} />} label="Inbound tons" value={fmtNumber(Math.round(m.inbound.tons))} sub={q.isLoading ? 'Loading…' : `${range.days} day${range.days === 1 ? '' : 's'}`} />
        <StatTile icon={<ArrowUpFromLine size={16} />} label="Outbound tons" value={fmtNumber(Math.round(m.outbound.tons))} sub={`${fmtNumber(m.outbound.loads)} loads`} />
        <StatTile icon={<DollarSign size={16} />} tone="warning" label="Disposal cost" value={fmtMoney(m.outbound.disposal_cost)} sub="outbound, at the Soft-Pak rates" />
        <StatTile icon={<Scale size={16} />} label="Cost per outbound ton" value={perTon(m.outbound.disposal_cost, m.outbound.tons)} sub="all destinations" />
        <StatTile icon={<Package size={16} />} label="Bales" value={fmtNumber(baled.bales)} sub={lbsPerBale ? `${fmtNumber(Math.round(lbsPerBale))} lbs per bale` : 'none in range'} />
      </div>

      <div style={{ ...card.sm, minWidth: 0 }}>
        <div style={s.cardHead}>Outbound by destination and material<span style={s.headNote}>Cost = Soft-Pak disposal rate × quantity (flat for per-load rates)</span></div>
        <div style={s.tableWrapFlat}>
          <table style={s.table}>
            <thead><tr>
              <th style={tbl.thSm}>Destination</th><th style={tbl.thSm}>Material</th>
              {['Tons', 'Loads', 'Disposal cost', '$/ton'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}
            </tr></thead>
            <tbody>
              {m.byDestination.map((x) => (
                <tr key={x.key} style={tbl.tr}>
                  <td style={tbl.tdSm}>{x.code}{x.name ? ` · ${x.name}` : ''}</td>
                  <td style={tbl.tdSm}>{x.material}</td>
                  <td style={tbl.tdRSm}>{fmtNumber(Math.round(x.tons))}</td>
                  <td style={tbl.tdRSm}>{fmtNumber(x.loads)}</td>
                  <td style={tbl.tdRSm}>{x.disposal_cost ? fmtMoney(x.disposal_cost) : '—'}</td>
                  <td style={tbl.tdRSm}>{x.disposal_cost ? perTon(x.disposal_cost, x.tons) : '—'}</td>
                </tr>
              ))}
              {!m.byDestination.length && <tr><td colSpan={6} style={{ ...tbl.tdSm, color: c.textFaint }}>{q.isLoading ? 'Loading…' : 'No outbound loads in this range.'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div style={s.grid2b}>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Inbound by material</div>
          <div style={s.tableWrapFlat}>
            <table style={s.table}>
              <thead><tr><th style={tbl.thSm}>Material</th>{['Tons', 'Loads', 'Charges'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}</tr></thead>
              <tbody>
                {m.byMaterialIn.map((x) => (
                  <tr key={x.key} style={tbl.tr}>
                    <td style={tbl.tdSm}>{x.key}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(Math.round(x.tons))}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(x.loads)}</td>
                    <td style={tbl.tdRSm}>{fmtMoney(x.charges)}</td>
                  </tr>
                ))}
                {!m.byMaterialIn.length && <tr><td colSpan={4} style={{ ...tbl.tdSm, color: c.textFaint }}>{q.isLoading ? 'Loading…' : 'No inbound loads in this range.'}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Weight per bale<span style={s.headNote}>Net lbs over bales, lines with a bale count</span></div>
          <div style={s.tableWrapFlat}>
            <table style={s.table}>
              <thead><tr><th style={tbl.thSm}>Material</th>{['Bales', 'Tons', 'Lbs per bale'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}</tr></thead>
              <tbody>
                {m.bales.map((x) => (
                  <tr key={x.key} style={tbl.tr}>
                    <td style={tbl.tdSm}>{x.key}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(x.bales)}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(Math.round(x.tons))}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(Math.round(x.net_lbs / x.bales))}</td>
                  </tr>
                ))}
                {!m.bales.length && <tr><td colSpan={4} style={{ ...tbl.tdSm, color: c.textFaint }}>{q.isLoading ? 'Loading…' : 'No baled lines in this range.'}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={{ ...card.sm, minWidth: 0 }}>
        <div style={s.cardHead}>Disposal rates<span style={s.headNote}>Maintained in Soft-Pak; these price the disposal cost above</span></div>
        {rates.isError && <div style={s.error}>{rates.error.message}</div>}
        <div style={s.tableWrapFlat}>
          <table style={s.table}>
            <thead><tr><th style={tbl.thSm}>Destination</th><th style={tbl.thSm}>Material</th><th style={tbl.thSm}>Per</th><th style={tbl.thRSm}>Rate</th></tr></thead>
            <tbody>
              {(rates.data?.rows || []).map((r) => (
                <tr key={`${r.destination_code}-${r.material_code}`} style={tbl.tr}>
                  <td style={tbl.tdSm}>{r.destination_code}{r.destination_name ? ` · ${r.destination_name}` : ''}</td>
                  <td style={tbl.tdSm}>{r.material_desc || r.material_code}</td>
                  <td style={tbl.tdSm}>{r.rate_type === 'Q' ? 'load' : 'ton'}</td>
                  <td style={tbl.tdRSm}>{money2(r.rate)}</td>
                </tr>
              ))}
              {!(rates.data?.rows || []).length && <tr><td colSpan={4} style={{ ...tbl.tdSm, color: c.textFaint }}>{rates.isLoading ? 'Loading…' : 'No rates for this facility.'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ ...card.sm, minWidth: 0 }}>
        <div style={s.cardHead}>
          Work orders<span style={s.headNote}>Work order lines with a material, by line date; dump tons as entered on the line</span>
        </div>
        <div style={{ ...s.toolbar, padding: '12px 18px 0' }}>
          <SelectOne label="Status" value={woStatus} onChange={setWoStatus} width={130}
                     options={[{ value: '', label: 'All' }, { value: 'Open', label: 'Open' }, { value: 'Closed', label: 'Closed' }]} />
          <span style={s.note}>{woTooLong ? 'Work orders cover up to 93 days. Choose a shorter range.' : wo.isLoading ? 'Loading…'
            : `${fmtNumber(woLines.length)} lines · ${fmtNumber(Math.round(woLines.reduce((t2, r) => t2 + n(r.dump_tons), 0)))} dump tons${wo.data?.truncated ? ' (first 50,000)' : ''}`}</span>
        </div>
        {wo.isError && <div style={s.error}>{wo.error.message}</div>}
        <div style={s.tableWrapFlat}>
          <table style={s.table}>
            <thead><tr>
              {['Date', 'Work order', 'Customer', 'Site', 'Material', 'Size', 'Status'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}
              <th style={tbl.thRSm}>Dump tons</th>
            </tr></thead>
            <tbody>
              {woLines.slice(0, 300).map((r, i) => (
                <tr key={`${r.work_order}-${r.material_code}-${i}`} style={tbl.tr}>
                  <td style={tbl.tdSm}>{fmtDate(r.line_date)}</td>
                  <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.work_order}</td>
                  <td style={tbl.tdSm}>{r.customer_name || r.customer_number}</td>
                  <td style={tbl.tdSm}>{r.site_name || '—'}</td>
                  <td style={tbl.tdSm}>{r.material_desc || r.material_code}</td>
                  <td style={tbl.tdSm}>{r.size_code || '—'}</td>
                  <td style={tbl.tdSm}><span style={r.status === 'Open' ? s.badgeWarn : s.badgeOk}>{r.status}</span></td>
                  <td style={tbl.tdRSm}>{n(r.dump_tons).toFixed(2)}</td>
                </tr>
              ))}
              {!woLines.length && !woTooLong && <tr><td colSpan={8} style={{ ...tbl.tdSm, color: c.textFaint }}>{wo.isLoading ? 'Loading…' : 'No work order lines in this range.'}</td></tr>}
            </tbody>
          </table>
        </div>
        {woLines.length > 300 && <div style={{ ...s.note, padding: '8px 18px' }}>Showing the first 300 of {fmtNumber(woLines.length)} lines.</div>}
      </div>
    </div>
  );
}

// ── Landfill (SBC): the four "SBC - Weekly Report" Crystal pages as one month ───────────
// The county contract counts in-county tons per site per month (page 3), Athens import goals
// are per month and judged over the fiscal year from July 1 (page 2), and C&D diversion and
// land use are paid per ton (page 4). Only the month and its holidays are chosen here; facility,
// cost center and the other filters above do not apply to county landfill data.
const LF_KEYS = ['ST', 'MV', 'VV', 'BA', 'LA'];
const TS_KEYS = ['HP', 'PH', 'TP', 'BB', 'TR', 'CA', 'BK', 'MO', 'NS'];
const CDSDP_RATE = 12.3;
const LAND_USE_RATE = 10;

const monthEnd = (month) => { const [y, m] = month.split('-').map(Number); return iso(new Date(Date.UTC(y, m, 0))); };
const prevMonth = (month) => { const [y, m] = month.split('-').map(Number); return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`; };
// Fiscal year starts July 1: July 2026 to June 2027 is FY 2027.
export function fyStart(month) {
  const [y, m] = month.split('-').map(Number);
  return `${m >= 7 ? y : y - 1}-07-01`;
}
// Mondays to Saturdays from the 1st through `through` (default: month end), less holidays
// (days of the month). Landfills are closed Sundays.
export function workingDays(month, holidays = [], through = monthEnd(month)) {
  let k = 0;
  for (let d = `${month}-01`; d <= through && d <= monthEnd(month); d = addDays(d, 1)) {
    if (new Date(`${d}T00:00:00Z`).getUTCDay() !== 0 && !holidays.includes(Number(d.slice(8)))) k += 1;
  }
  return k;
}
export function parseHolidays(text) {
  return [...new Set(String(text).split(/[\s,]+/).map(Number).filter((v) => Number.isInteger(v) && v >= 1 && v <= 31))];
}

// Rows from /sbc-daily (FY start or the prior month, whichever is earlier, to the month's end)
// folded into the month page.
export function sbcSummary(rows, month, holidays = []) {
  const inMonth = (r) => String(r.date_in).slice(0, 7) === month;
  const pm = prevMonth(month);
  const lastDay = rows.filter(inMonth).reduce((mx, r) => (String(r.date_in).slice(0, 10) > mx ? String(r.date_in).slice(0, 10) : mx), '');
  const total = workingDays(month, holidays);
  const elapsed = lastDay ? workingDays(month, holidays, lastDay) : 0;
  const factor = elapsed ? total / elapsed : 0;
  const fy = fyStart(month);
  const fyMonthsBefore = (Number(month.slice(0, 4)) * 12 + Number(month.slice(5))) - (Number(fy.slice(0, 4)) * 12 + 7);
  const fyGoalShare = fyMonthsBefore + (total ? elapsed / total : 0);
  const keys = [...LF_KEYS, ...TS_KEYS];
  const sites = keys.map((key) => {
    const mine = rows.filter((r) => r.column_key === key);
    const sum = (list, f) => list.reduce((t2, r) => t2 + n(r[f]), 0);
    const mtd = mine.filter(inMonth);
    const fyRows = mine.filter((r) => String(r.date_in).slice(0, 10) >= fy && String(r.date_in).slice(0, 7) <= month);
    const meta = mine[0] || {};
    const inCounty = sum(mtd, 'in_county_in');
    const contract = n(meta.in_county_monthly_tons);
    const goal = n(meta.import_msw_monthly_goal);
    return {
      key, group: LF_KEYS.includes(key) ? 'LF' : 'TS', name: meta.site_name || key,
      tonsIn: sum(mtd, 'tons_in'), inCounty, projected: inCounty * factor,
      prior: sum(mine.filter((r) => String(r.date_in).slice(0, 7) === pm), 'in_county_in'),
      contract, variance: contract ? inCounty * factor - contract : null,
      importMsw: sum(mtd, 'import_msw_in'), importAdc: sum(mtd, 'import_adc_in'),
      fyImportMsw: sum(fyRows, 'import_msw_in'), fyGoal: goal ? goal * fyGoalShare : null,
      scor: sum(mtd, 'scor_in'), gcr: sum(mtd, 'gcr_in'), cd: sum(mtd, 'cd_tons'),
    };
  });
  const landUse = rows.filter((r) => r.column_key === 'LU' && inMonth(r)).reduce((t2, r) => t2 + n(r.tons_in), 0);
  const group = (g) => sites.filter((x) => x.group === g).reduce((acc, x) => {
    for (const f of ['tonsIn', 'inCounty', 'projected', 'prior', 'contract']) acc[f] += x[f];
    return acc;
  }, { tonsIn: 0, inCounty: 0, projected: 0, prior: 0, contract: 0 });
  const cd = sites.reduce((t2, x) => t2 + x.cd, 0);
  const latest = rows.reduce((mx, r) => (String(r.date_in).slice(0, 10) > mx ? String(r.date_in).slice(0, 10) : mx), '');
  return {
    lastDay, latest, total, elapsed, sites, lf: group('LF'), ts: group('TS'),
    cd, cdDollars: cd * CDSDP_RATE, landUse, landUseDollars: landUse * LAND_USE_RATE,
    scor: sites.reduce((t2, x) => t2 + x.scor, 0), gcr: sites.reduce((t2, x) => t2 + x.gcr, 0),
  };
}

function Landfill() {
  const [month, setMonth] = useState(pacificToday().slice(0, 7));
  const [holidayText, setHolidayText] = useState('');
  const holidays = useMemo(() => parseHolidays(holidayText), [holidayText]);
  const from = [fyStart(month), `${prevMonth(month)}-01`].sort()[0];
  const params = { from, to: monthEnd(month) };
  const q = useQuery({
    queryKey: ['scale-pak', 'sbc-daily', params],
    queryFn: () => api.get('/scale-pak/sbc-daily', { params }),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const x = useMemo(() => sbcSummary(q.data?.rows || [], month, holidays), [q.data, month, holidays]);
  const fmtT = (v) => fmtNumber(Math.round(v));
  const variance = (v) => (v == null ? '—' : <span style={{ color: v < 0 ? c.dangerText : c.successText }}>{v > 0 ? '+' : ''}{fmtT(v)}</span>);
  const groupRow = (label, g) => (
    <tr style={{ ...tbl.tr, fontWeight: 600 }}>
      <td style={tbl.tdSm}>{label}</td><td style={tbl.tdRSm}>{fmtT(g.tonsIn)}</td><td style={tbl.tdRSm}>{fmtT(g.inCounty)}</td>
      <td style={tbl.tdRSm}>{fmtT(g.projected)}</td><td style={tbl.tdRSm}>{fmtT(g.prior)}</td><td style={tbl.tdRSm}>{fmtT(g.contract)}</td>
      <td style={tbl.tdRSm}>{variance(g.contract ? g.projected - g.contract : null)}</td>
    </tr>
  );
  const siteRows = (g) => x.sites.filter((r) => r.group === g).map((r) => (
    <tr key={r.key} style={tbl.tr}>
      <td style={tbl.tdSm} title={r.key}>{r.name}</td><td style={tbl.tdRSm}>{fmtT(r.tonsIn)}</td><td style={tbl.tdRSm}>{fmtT(r.inCounty)}</td>
      <td style={tbl.tdRSm}>{fmtT(r.projected)}</td><td style={tbl.tdRSm}>{fmtT(r.prior)}</td><td style={tbl.tdRSm}>{r.contract ? fmtT(r.contract) : '—'}</td>
      <td style={tbl.tdRSm}>{variance(r.variance)}</td>
    </tr>
  ));
  return (
    <div style={s.panel}>
      <div style={s.toolbar}>
        <TextField label="Month" type="month" value={month} onChange={(v) => v && setMonth(v)} width={160} />
        <TextField label="Holidays (days of the month)" value={holidayText} onChange={setHolidayText} placeholder="e.g. 11, 26" width={210} />
        <span style={s.note}>
          {q.isLoading ? 'Loading…' : x.lastDay
            ? `Data through ${fmtDate(x.lastDay)} · ${x.elapsed} of ${x.total} working days (Sundays and holidays off) · facility and other filters do not apply here`
            : 'No landfill tons for this month yet.'}
        </span>
      </div>
      {/* A failed load or a month the extract does not reach yet shows a message, not a page of
          zeros: with no tons every site's variance equals its whole contract, which reads as a
          shortfall. LUISV.SBC is refreshed about monthly, so the newest months are often empty. */}
      {q.isError && <div style={s.error}>The landfill figures could not be loaded: {q.error.message}</div>}
      {!q.isError && !q.isLoading && !x.lastDay && (
        <div style={s.empty}>
          No SBC data for {fmtMonth(`${month}-01`)} yet.{' '}
          {x.latest ? `The county extract (LUISV.SBC) currently runs through ${fmtDate(x.latest)}.` : 'The county extract (LUISV.SBC) has nothing for this fiscal year yet.'}
        </div>
      )}
      {!q.isError && (q.isLoading || x.lastDay) && (<>
      <div style={s.tiles}>
        <StatTile icon={<Mountain size={16} />} label="Landfill tons in" value={fmtT(x.lf.tonsIn)} sub="month to date, 5 landfills" />
        <StatTile icon={<TrendingUp size={16} />} label="Projected in-county" value={fmtT(x.lf.projected + x.ts.projected)} sub="at this month's daily pace" />
        <StatTile icon={<Target size={16} />} tone={x.lf.projected + x.ts.projected >= x.lf.contract + x.ts.contract ? 'success' : 'warning'} label="Contract in-county"
                  value={fmtT(x.lf.contract + x.ts.contract)} sub="tons per month, all sites" />
        <StatTile icon={<Recycle size={16} />} tone="success" label="C&D diversion (CDSDP)" value={fmtMoney(x.cdDollars)} sub={`${fmtT(x.cd)} t at $${CDSDP_RATE}/t`} />
        <StatTile icon={<DollarSign size={16} />} tone="success" label="Land use" value={fmtMoney(x.landUseDollars)} sub={`${fmtT(x.landUse)} t at $${LAND_USE_RATE}/t`} />
      </div>

      <div style={{ ...card.sm, minWidth: 0 }}>
        <div style={s.cardHead}>In-county tons against the contract<span style={s.headNote}>In-county = inbound less Athens imports · projected = month to date ÷ working days so far × working days in month</span></div>
        <div style={s.tableWrapFlat}>
          <table style={s.table}>
            <thead><tr>
              <th style={tbl.thSm}>Site</th>
              {['Tons in', 'In-county MTD', 'Projected', 'Prior month', 'Contract', 'Variance'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}
            </tr></thead>
            <tbody>
              {siteRows('LF')}
              {groupRow('Landfills', x.lf)}
              {siteRows('TS')}
              {groupRow('Transfer stations and transfers in', x.ts)}
            </tbody>
          </table>
        </div>
      </div>

      <div style={s.grid2b}>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Athens imports<span style={s.headNote}>MSW goal is per month, judged over the fiscal year from {fmtDate(fyStart(month))}</span></div>
          <div style={s.tableWrapFlat}>
            <table style={s.table}>
              <thead><tr><th style={tbl.thSm}>Landfill</th>{['ADC MTD', 'MSW MTD', 'MSW fiscal YTD', 'Goal to date', 'Variance'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}</tr></thead>
              <tbody>
                {x.sites.filter((r) => r.group === 'LF').map((r) => (
                  <tr key={r.key} style={tbl.tr}>
                    <td style={tbl.tdSm}>{r.name}</td>
                    <td style={tbl.tdRSm}>{fmtT(r.importAdc)}</td>
                    <td style={tbl.tdRSm}>{fmtT(r.importMsw)}</td>
                    <td style={tbl.tdRSm}>{fmtT(r.fyImportMsw)}</td>
                    <td style={tbl.tdRSm}>{r.fyGoal == null ? '—' : fmtT(r.fyGoal)}</td>
                    <td style={tbl.tdRSm}>{variance(r.fyGoal == null ? null : r.fyImportMsw - r.fyGoal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Billed to SCOR and Grand Central<span style={s.headNote}>Inbound tons, month to date</span></div>
          <div style={s.tableWrapFlat}>
            <table style={s.table}>
              <thead><tr><th style={tbl.thSm}>Site</th><th style={tbl.thRSm}>SCOR</th><th style={tbl.thRSm}>Grand Central</th></tr></thead>
              <tbody>
                {x.sites.filter((r) => r.scor || r.gcr).map((r) => (
                  <tr key={r.key} style={tbl.tr}><td style={tbl.tdSm}>{r.name}</td><td style={tbl.tdRSm}>{fmtT(r.scor)}</td><td style={tbl.tdRSm}>{fmtT(r.gcr)}</td></tr>
                ))}
                <tr style={{ ...tbl.tr, fontWeight: 600 }}><td style={tbl.tdSm}>Total</td><td style={tbl.tdRSm}>{fmtT(x.scor)}</td><td style={tbl.tdRSm}>{fmtT(x.gcr)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}

const s = {
  live:      { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: c.successText, background: c.successTint, borderRadius: '999px', padding: '2px 9px' },
  dot:       { width: 6, height: 6, borderRadius: '50%', background: c.success },
  filters:   { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', padding: '14px 18px' },
  field:     { display: 'flex', flexDirection: 'column', gap: '4px' },
  label:     { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: c.textMuted },
  seg:       { display: 'inline-flex', height: '32px', border: `1px solid ${c.border}`, borderRadius: '6px', overflow: 'hidden', background: c.surface },
  segBtn:    { border: 0, borderRight: `1px solid ${c.border}`, background: 'transparent', color: c.textMuted, padding: '0 11px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' },
  segOn:     { background: c.navy, color: '#fff', fontWeight: 600 },
  tabRow:    { display: 'flex', borderBottom: `1px solid ${c.border}`, padding: '0 18px', overflowX: 'auto' },
  // Longhand bottom border: the active tab changes only its color, and mixing a shorthand with
  // a longhand on rerender leaves stale styles (React warns, as StatTile notes).
  tab:       { background: 'none', borderTop: 0, borderLeft: 0, borderRight: 0, padding: '10px 2px', marginRight: '22px', fontSize: '13px', fontWeight: 600, color: c.textFaint, borderBottomWidth: '2px', borderBottomStyle: 'solid', borderBottomColor: 'transparent', marginBottom: '-1px', cursor: 'pointer', fontFamily: 'inherit' },
  tabOn:     { color: c.navy, borderBottomColor: c.navy },
  panel:     { padding: '18px', display: 'grid', gap: '16px' },
  // 160px, not Inventory's 190: six tiles then fit one row on a laptop instead of leaving one alone.
  tiles:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' },
  grid2:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '16px' },
  grid2b:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '16px' },
  cardHead:  { ...card.head, borderRadius: '10px 10px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  headNote:  { fontSize: '11px', fontWeight: 400, color: c.textFaint },
  chartBody: { padding: '14px 18px 12px' },
  legend:    { display: 'inline-flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', fontSize: '11px', fontWeight: 400, color: c.textMuted },
  legendItem:{ display: 'inline-flex', alignItems: 'center', gap: '5px' },
  swatch:    { display: 'inline-block', width: 10, height: 10, borderRadius: 2 },
  ranked:    { display: 'grid', gap: '4px' },
  rankRow:   { display: 'grid', gridTemplateColumns: 'minmax(90px, 170px) minmax(0, 1fr) 64px', gap: '8px', alignItems: 'center', background: 'none', border: 0, padding: '3px 2px', cursor: 'pointer', textAlign: 'left', borderRadius: '4px', fontFamily: 'inherit' },
  rankLabel: { fontSize: '11.5px', color: c.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rankTrack: { height: '14px' },
  rankBar:   { display: 'block', height: '100%', borderRadius: '0 4px 4px 0' },
  rankValue: { fontSize: '10.5px', color: c.textMuted, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  heatRow:   { display: 'grid', gridTemplateColumns: `32px repeat(${HOURS.length}, minmax(0, 1fr))`, gap: '2px', alignItems: 'center', marginBottom: '2px' },
  heatHour:  { fontSize: '10px', color: chart.axisText, textAlign: 'center' },
  heatDay:   { fontSize: '10px', color: c.textMuted },
  heatCell:  { height: '20px', borderRadius: '2px' },
  toolbar:   { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
  search:    { flex: '1 1 260px', minWidth: 0, height: '32px', padding: '0 10px', border: `1px solid ${c.border}`, borderRadius: '6px', fontSize: '12px', fontFamily: 'inherit' },
  tableWrap: { overflowX: 'auto', border: `1px solid ${c.border}`, borderRadius: '10px' },
  tableWrapFlat: { overflowX: 'auto' },
  badgeOk:   { fontSize: '11px', fontWeight: 600, color: c.successText, background: c.successTint, borderRadius: '999px', padding: '2px 9px' },
  badgeWarn: { fontSize: '11px', fontWeight: 600, color: c.warningText, background: c.warningTint, borderRadius: '999px', padding: '2px 9px' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  voidRow:   { color: c.textFaint, textDecoration: 'line-through' },
  note:      { fontSize: '12px', color: c.textMuted },
  empty:     { fontSize: '12px', color: c.textFaint, padding: '24px 0', textAlign: 'center' },
  error:     { margin: '12px 18px 0', padding: '10px 12px', borderRadius: '8px', background: c.dangerTint, color: c.dangerText, fontSize: '12px' },
};
